import React, { useCallback } from 'react'
import { I18n } from '@front/volcanion'
import { GeoUtils, TimeUtils } from '@front/volcanion/utils'
import errors from '@front/volcanion/classes/error'

import _ from "lodash"
import moment from 'moment'
import { Button } from "@mui/material"

import { OrangeContact } from '../..'

const SuggestionButton = ({ onClose, argument, suggestion, setFormValues }) => {
  const handleClick = useCallback(() => {
    setFormValues([{ field: argument, value: suggestion }])
    onClose()
  }, [argument, suggestion, setFormValues, onClose])

  return (
    <Button
      onClick={handleClick}
      sx={{ color: 'white', textDecoration: 'underline' }}
      variant="text"
    >
      {I18n.t('action.apply')}
    </Button>
  )
}

import {
  getLoadsInfo,
  getOrderInfo,
  getBillingData,
  getAddressInfo,
  getReservationInfo,
  getUser,
  verifyData,
  getBillingSectionFromRecord
} from './utils'

class Callbacks {
  static getEmptyFormHandler() {
    return function getEmptyForm() {
      return {
        schedule_type: 'immediate',
        vehicle_count: 1,
        load_count: 1,
        estimation_type: 'estimated',
        exclude_heatmap: false,
        bypass_heatmap_cascade: false,
        bonus_heatmap: false,
        load_type: 'user',
      }
    }
  }
  static getEmptyStateHandler(domain) {
    return function getEmptyState() {
      return {
        isB2B: false,
        confirmed: true,
        displayRoundTrip: false,
        save_comment: { source: false, destination: false },
        favorite_address: { source: false, destination: false },
        channel: domain,
        isExperimentalUnmountField: true
      }
    }
  }
  static recordToFormHandler(isOrderDuplicate) {
    return function recordToForm(record) {
      const company = record?.service?.contract?.company
      const companyuser = _.find(record?.do?.user_companies, user => user?.company === company)?.companyuser_id || null
      return {
        ...getBillingSectionFromRecord(record),
        user: {
          companyuser,
          gsm: !!record?.service ? record?.calling_number : record?.do?.auth?.gsm,
          calling_number: record?.calling_number,
          client_number: record?.do?.customerinfo?.client_number,
          first_name: record?.do?.info?.first_name,
          last_name: record?.do?.info?.last_name,
          name: record?.do?.customerinfo?.name,
          address: record?.do?.info?.address,
          comment_to_call_taker: record?.do?.customerinfo?.comment_to_call_taker,
          application: record?.do?.auth?.application,
          commercial_formula: record?.service ? record?.service?.contract?.formula : record?.do?.commercial_formula,
          companyservice: record?.service?.companyservice_id,
          company: record?.service?.contract?.company,
          companycontract: record?.service?.contract?.companycontract_id,
          password: record?.service?.password,
        },
        ridemode: record?.mode?.ridemode_id,
        ridemode_duration: record?.info?.duration,
        driver_comment: record?.driver_comment,
        requestedAt: TimeUtils.getDetailsMomentFront(record?.requestedAt, record?.source),
        returnAt: TimeUtils.getDetailsMomentFront(record?.returnAt, record?.source),
        schedule_type: record?.schedule_type,
        commercial_package: record?.commercial_package?.commercial_package_id,
        poi: record?.source?.poi?.poi_id,
        source: GeoUtils.getFormattedObject(record?.source),
        destination: GeoUtils.getFormattedObject(record?.destination),
        loads: {
          src_contact: record?.loads?.[0]?.src_contact,
          src_phone: record?.loads?.[0]?.src_phone,
          dest_contact: record?.loads?.[0]?.dest_contact,
          dest_phone: record?.loads?.[0]?.dest_phone,
        },
        info: {
          src_comment: record?.info?.src_comment,
          dest_comment: record?.info?.dest_comment,
        },
        reservationinfo: {
          name: record?.reservationinfos?.[0]?.name,
          origin: record?.reservationinfos?.[0]?.origin,
        },
        comment_to_call_taker: record?.customerinfo?.comment_to_call_taker,
        load_type: record?.commercial_package?.load_type,
        estimation_type: record?.quotation?.type,
        manual_price: record?.quotation?.type === 'special' ? record?.quotation?.base : undefined,
        driver_opts: _.map(record?.driver_opts, 'user_option_id'),
        vehicle_opts: _.map(record?.vehicle_opts, 'vehicle_option_id'),
        payment_type: !!isOrderDuplicate ? undefined : record?.payment_type,
        customer_ref: record?.customer_ref,
        order_doc: record?.order_doc,
        load_count: record?.passenger_count || record?.package_count,
        has_luggage: record?.has_luggage,
        exclude_heatmap: record?.job?.exclude_heatmap,
        bypass_heatmap_cascade: record?.job?.bypass_heatmap_cascade,
        bonus_heatmap: record?.job?.bonus_heatmap,
        vehicle_count: 1,
        voucher: record?.voucher?.code,
      }
    }
  }

  static recordToStateHandler(isOrderDuplicate, domain) {
    return function recordToState(record) {
      return {
        confirmed: record?.confirmed,
        isManagingRecord: !isOrderDuplicate,
        isReadOnly: !isOrderDuplicate,
        channel: isOrderDuplicate ? domain : record?.channel,
        isOrderDuplicate,
        displayRoundTrip: !!record?.returnAt,
        isB2B: !!_.get(record, 'service'),
        guest: false,
        company_id: _.get(record, 'service.contract.company'),
        user_id: _.get(record, 'do.user_id'),
        billing: false,
        hasJobPayment: _.has(record, 'job.payment'),
        isMapOpen: false,
        voucher_id: record?.voucher?.voucher_id,
        call_id: _.get(record, 'info.call_id'),
        job_id: _.get(record, 'job.job_id'),
        isManagingBillingRecord: false,
        source_reservationinfo_id: _.get(record, 'source.poi.type.reservationinfo'),
        order_reservationinfo_id: _.get(record, 'reservationinfos.0.order_reservationinfo_id'),
        meeting_point: {
          translated: _.get(record, 'info.meeting_point_translated')
        },
        driver_instruction: {
          translated: _.get(record, 'info.driver_instruction_translated')
        },
        details: {
          source: _.get(record, 'source'),
          destination: _.get(record, 'destination')
        },
        isGrouped: (_.get(record, 'job.orders') || []).length >= 2,
        isDurationRideMode: record?.mode?.config?.display_duration_field,
        ridemode_info: {
          nights: record?.info?.night,
          duration: record?.info?.duration
        },
        psp: record?.transaction?.psp,
        selected_user: (!!isOrderDuplicate && !!record?.service) ? record?.do?.user_id : undefined,
        isExperimentalUnmountField: true,
        save_comment: {
          source: !!record?.source?.comment,
          destination: !!record?.destination?.comment
        }
      }
    }
  }
  static formToRecordHandler(CODE_INTERNATIONAL, getEstimation) {
    return function formToRecord(values, extra, meta, state) {
      const { isManagingRecord } = state

      const estimation = getEstimation(values?.commercial_package, values?.estimation_type)
      const reservationinfos = getReservationInfo(values, state)
      const billing_data = getBillingData(values, state)
      const info = getOrderInfo(values, state)
      const loads = getLoadsInfo(values, state, { CODE_INTERNATIONAL })
      const source = getAddressInfo(values, state, { prefix: 'source', save_comment: state?.save_comment?.source })
      const destination = getAddressInfo(values, state, { prefix: 'destination', save_comment: state?.save_comment?.destination })

      const requestedAt = verifyData(_.has(values, 'requestedAt'), TimeUtils.getDetailsMomentBack(values?.requestedAt, state?.details?.source))
      const returnAt = verifyData(_.has(values, 'returnAt'), TimeUtils.getDetailsMomentBack(values?.returnAt, state?.details?.source))

      return _.merge({
        requestedAt,
        returnAt,
        source,
        destination,
        info,
        loads,
        confirmed: extra?.forceConfirmed ?? state?.confirmed,
        commercial_package: verifyData(!!estimation?.estimation_id, values?.commercial_package),
        driver_opts: verifyData(_.has(values, 'driver_opts'), _.compact(values?.driver_opts)),
        vehicle_opts: verifyData(_.has(values, 'vehicle_opts'), _.compact(values?.vehicle_opts)),
        customer_ref: values?.customer_ref,
        driver_comment: values?.driver_comment,
        order_doc: values?.order_doc,
        payment_type: values?.payment_type,
        voucher: state?.voucher_id,
        schedule_type: values?.schedule_type,
        calling_number: values?.user?.gsm,
        passenger_count: values?.passenger_count,
        package_count: values?.package_count,
        has_luggage: values?.has_luggage,
        estimation_id: estimation?.estimation_id,
        mode: values?.ridemode,
        handling_duration: values?.quotation?.handling_duration,
        // BILLING
        billing_status: values?.billing_status,
        billing_customer_comment: values?.billing_customer_comment,
        billing_group_comment: values?.billing_member_comment,
        payment_ref: values?.payment_ref
      },
        billing_data,
        reservationinfos?.[0]?.origin ? { reservationinfos } : {},
        !isManagingRecord ? {
          do: getUser(values, state),
          service: values?.user?.companyservice,
        } : {},
      )
    }
  }
  static formToOptionsHandler() {
    return function formToOptions(values, extra, meta, state) {
      return ({
        repeat: _.get(values, 'vehicle_count'),
        single: (_.get(values, 'vehicle_count') || 1) === 1,
        override_multiple_order_check: true,
        load_type: _.get(values, 'load_type'),
        load_count: _.get(values, 'load_count'),
        manual_price: _.get(values, 'manual_price'),
        estimation_type: values?.estimation_type,
        application: _.get(values, 'user.application'),
        disable_traffic_validate: true,
        disable_planned_delay: !!state?.isManagingRecord,
        exclude_heatmap: values?.exclude_heatmap,
        bypass_heatmap_cascade: values?.bypass_heatmap_cascade,
        bonus_heatmap: values?.bonus_heatmap,
        force_duplicate_order: !!extra?.force_duplicate_order,
        force_limit_exceeded: !!extra?.force_limit_exceeded
      })
    }
  }

  static onSubmitSuccessHandler() {
    return function onSubmitSuccess(result, values, extra, meta, state) {
      console.log("🚀 ~ file: callbacks.js ~ line 166 ~ Callbacks ~ onSubmitSuccess ~ result", result)
    }
  }
  static onSubmitFailedHandler(openDialog) {
    return function onSubmitFailed(err, values, extra, meta, state) {
      if (err?.code === 'DUPLICATE_ORDER')
        openDialog('show_duplicate_orders', {
          title: I18n.t('order.popup.duplicate_orders.title'),
          description: I18n.t('order.popup.duplicate_orders.description'),
          order_ids: err?.context?.order_ids,
        })
      else if (err?.code === 'LIMIT_EXCEEDED')
        openDialog('order_limit_exceeded', {
          title: I18n.t('order.popup.limit_exceeded.title'),
          description: I18n.t('order.popup.limit_exceeded.description', { message: err?.context?.message })
        })
    }
  }

  static getFailedNotificationHandler() {
    return function getFailedNotification(err, values, extra, meta, state) {
      const code = err.getCode()
      const context = err.getContext()
      const message = (err instanceof errors.MismatchError && context?.data?.message) || I18n.t('order.failed')
      if (code === 'UNAUTHORIZED_SERVICE_SCHEDULE') {
        return [
          I18n.t('unauthorized_schedule.popup.description', {
            order_type: I18n.t(`order.${values?.schedule_type}.longLabel`, { count: 1 }),
            requestedAt: moment(values?.requestedAt).format(values?.schedule_type === 'immediate' ? 'HH[h]mm[min]' : `[${I18n.t('prefix.the')}] DD/MM/YYYY HH[h]mm[min]`)
          })
          , { variant: 'error' }
        ]
      }
      else
        return [message, { variant: 'error' }]
    }
  }
  static handleValidateNotificationHandler(openNotification, setFormValues) {
    return async function handleValidateNotification(data) {
      _.map(data, ({ message, type, code, context }) => {
        const { argument, suggestion } = context || {}
        const action = !!suggestion
          ? (onClose) => (
            <SuggestionButton
              onClose={onClose}
              argument={argument}
              suggestion={suggestion}
              setFormValues={setFormValues}
            />
          )
          : undefined
        !!message && openNotification(message, {
          action,
          variant: type,
          id: `validation-${code}`
        })
      })
    }
  }

  static handleWaitingOrangeHandler() {
    return async function handleWaitingOrange() {
      return OrangeContact.execute('GET', 'clickToCall', {
        queueName: 'WSFLEET',
        transfer: true,
        autoswitch: true,
        autowrapup: true
      })
    }
  }
  static handleOrangeCallHandler() {
    return async function handleOrangeCall(phoneNumber) {
      return OrangeContact.execute('GET', 'clickToCall', { phoneNumber })
    }
  }
  static handleCallHandler(EXTERNAL_PHONE_SERVICE, orangeCall) {
    return async function call(number) {
      if (!number) return
      if (EXTERNAL_PHONE_SERVICE === 'aws') {
        const endpoint = connect.Endpoint.byPhoneNumber(number)
        const agent = new connect.Agent()
        const contacts = agent.getContacts()
        if (_.isEmpty(contacts))
          agent.connect(endpoint)
        else if (contacts.length === 1)
          contacts[0].addConnection(endpoint)
      }
      else if (EXTERNAL_PHONE_SERVICE === 'orange') {
        const phoneNumber = encodeURIComponent(number)
        await orangeCall(phoneNumber)
      }
    }
  }
  static handleClickCallCenterHandler(reset, setIsWaiting) {
    return function handleClickCallCenter() {
      reset()
      setIsWaiting(true)
    }
  }
  static handleClickMapHandler(isMapOpen, setFormState) {
    return function handleClickMap() {
      if (!isMapOpen) setTimeout(() => { window.scrollTo(0, document.body.scrollHeight) }, 500)
      setFormState({ isMapOpen: !isMapOpen })

    }
  }
  static handleCancelHandler(cancel, order, openNotification, handleOpenCancelConfirmationDialog, isGrouped) {
    return async function handleCancel(force, reason, cancelation_comment) {
      const { confirmed, order_id } = order

      if ((_.isBoolean(force) && !!force) || !confirmed) {
        cancel({ order_id, reason, cancelation_comment })
        openNotification(I18n.t('event.order_canceled'), { variant: 'success' })
      }
      else if (!force && !!confirmed || isGrouped) {
        handleOpenCancelConfirmationDialog(isGrouped)
      }
    }
  }

  static handleNewOrderHandler(navigate) {
    return function handleNewOrder() {
      navigate('/order/create')
    }
  }


  static handleInvoiceDownloadHanlder(downloadInvoice, openNotification) {
    return async function handleInvoiceDownload() {
      try {
        await downloadInvoice()
      }
      catch (err) {
        console.warn("Failed to download file", err)
        openNotification('Facture non disponible', { variant: 'error' })
      }
    }
  }

  static isDownloadAvailableStatusHandler(isDownloadAvailable, setFormState) {
    return async function isDownloadAvailableStatus(order_id) {
      try {
        const response = await isDownloadAvailable({ order_id, type: 'invoice' })
        setFormState({ displayDownloadBillingButton: !!response?.status })

      } catch (err) {
        console.error('failed to get download available status', err)
        setFormState({ displayDownloadBillingButton: false })
      }
    }
  }
  static getModifyFieldsHandler(order, isOrderDuplicate) {
    return function getModifyFields() {
      const canModifyAllFields = !!isOrderDuplicate
        || _.includes(['created', 'standby'], order?.status)
        || (_.includes(['attributed'], order?.status) && moment(order?.requestedAt).isAfter(moment()))

      return canModifyAllFields
        ? []
        : ['customer_ref', 'order_doc', 'info.src_comment', 'info.dest_comment', 'driver_comment']
    }
  }
  static handleOrderCopyHandler(record_id) {
    return function handleOrderCopy() {
      window.open(`/order/create?order_id=${record_id}`, '_blank')
    }
  }
  static beforeSubmitHanbdler(openNotification, openDialog, allowed_driver_options, allowed_vehicle_options) {
    return function beforeSubmit(values, extra, meta, state) {
      const driver_opts = _.find(meta, ['field', 'driver_opts'])?.initial
      const vehicle_opts = _.find(meta, ['field', 'vehicle_opts'])?.initial


      if (!!state?.isB2B && !state?.hasCompanyAccess && !!values?.companyservice_id) {
        openNotification(I18n.t('password.incorrect'), { variant: 'error' })
        return false
      }

      if (
        !_.every(driver_opts, driver_opt => _.includes(allowed_driver_options, driver_opt)) ||
        !_.every(vehicle_opts, vehicle_opt => _.includes(allowed_vehicle_options, vehicle_opt))
      ) {
        if (!!extra?.force_options)
          return true

        openDialog('confirmation_options', {
          title: I18n.t('order.popup.unauthorized_options.title'),
          driver_options_not_allowed: _.difference(driver_opts, allowed_driver_options),
          vehicle_options_not_allowed: _.difference(vehicle_opts, allowed_vehicle_options)
        })
        return false
      }
      return true
    }
  }
}

export default Callbacks
